import { useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { generatePath, useOutlet, useSearchParams } from 'react-router-dom';
import { ErrorDisplay } from 'components/molecules';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { parseStringIntoDayjsDate } from 'utils/parsers/dateTime/parseStringIntoDayjsDate';
import { getMonthFromDayjsMonth } from 'utils/getMonthFromDayjsMonth';
import { useDateFilter } from 'hooks/useDateFilter/useDateFilter';
import { DATE_FILTER_KEY } from 'consts/filters/common/filtersKeys';
import { CalendarDayModal, CalendarFilters } from 'components/templates/calendar/';
import { appPaths } from 'consts/paths/paths';
import { AppTitle, ComponentBlockingAppLoader } from 'components/atoms';
import { AppCalendarContext } from 'contexts/AppCalendarContext';
import { CalendarApi, EventClickArg } from '@fullcalendar/core';
import { parseCalendarEventDetailedDtoIntoCalendarComponentEvent } from 'utils/parsers/calendar/parseCalendarEventDetailedDtoIntoCalendarComponentEvent';
import { parseDayjsDateIntoString } from 'utils/parsers/dateTime/parseDayjsDateIntoString';
import { FULL_MONTH_FORMAT } from 'consts/dateFormats';
import { calendarTexts } from 'consts/text';
import { StyledCalendar } from 'components/molecules/StyledCalendar/StyledCalendar';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';

export const CalendarPage = () => {
    const outlet = useOutlet();
    const calendarRef = useRef();
    const { statefulNavigate } = useNavigateWithState();

    const [dayModalOpen, setDayModalOpen] = useState<boolean>(false);

    const [searchParams] = useSearchParams();
    const { dateFilterValue, handleChangeDateFilter, handleClickNextOrPrevDate } = useDateFilter({
        filterKey: DATE_FILTER_KEY,
    });

    const [modalSelectedDate, setModalSelectedDate] = useState<string>(
        searchParams.get('date') ?? parseDayjsDateIntoString(dayjs(), 'YYYY-MM-DD'),
    );
    const contextValue = useMemo(
        () => ({ selectedDate: modalSelectedDate, setSelectedDate: setModalSelectedDate }),
        [modalSelectedDate],
    );

    const selectedDate = dateFilterValue ? parseStringIntoDayjsDate(dateFilterValue) : dayjs();
    const selectedYear = selectedDate.year();
    const selectedMonth = getMonthFromDayjsMonth(selectedDate.month());

    const {
        data: eventsData,
        isFetching: isEventsDataFetching,
        isError: isFetchEventsDataError,
    } = useAppQuery(
        'MONTHLY_EVENTS',
        [selectedMonth, selectedYear],
        () =>
            StaffHTTPService.calendar.getMonthlyEvents({
                month: selectedMonth,
                year: selectedYear,
            }),
        {
            refetchOnWindowFocus: false,
        },
    );

    const calendarParsedEvents = useMemo(() => {
        if (!eventsData) {
            return [];
        }
        return eventsData.map(parseCalendarEventDetailedDtoIntoCalendarComponentEvent);
    }, [eventsData]);

    const handleDateClick = (props: { dateStr: string }) => {
        // eslint-disable-next-line react/prop-types
        setModalSelectedDate(props?.dateStr);
        setDayModalOpen(true);
    };

    const handleEventClick = (event: EventClickArg) => {
        setModalSelectedDate(event.event.startStr);
        statefulNavigate(
            generatePath(appPaths.app.calendar.details.pickedEvent, {
                eventId: event.event.id,
            }),
        );
    };

    const handleMoreClick = (props: { date: Date }) => {
        // eslint-disable-next-line react/prop-types
        setModalSelectedDate(parseDayjsDateIntoString(dayjs(props.date), 'YYYY-MM-DD'));
        setDayModalOpen(true);
    };

    const monthName = dayjs()
        .month(selectedMonth - 1)
        .format(FULL_MONTH_FORMAT);

    const displayTitleDate = `${
        monthName.charAt(0).toUpperCase() + monthName.slice(1)
    } ${selectedYear}`;

    const calendarPageTitle = (
        <AppTitle level={3} margin={0} marginBottom={16} textAlign="center">
            {displayTitleDate}
        </AppTitle>
    );

    if (isFetchEventsDataError) {
        return <ErrorDisplay />;
    }

    return (
        <AppCalendarContext.Provider value={contextValue}>
            {outlet}
            {dayModalOpen && (
                <CalendarDayModal
                    open={dayModalOpen}
                    monthlyFetchFunction={StaffHTTPService.calendar.getMonthlyEvents}
                    dailyFetchFunction={StaffHTTPService.calendar.getDailyEvents}
                    onCancel={() => setDayModalOpen(false)}
                />
            )}
            {calendarPageTitle}
            <CalendarFilters
                // @ts-ignore
                calendarApi={() => calendarRef?.current?.getApi() as CalendarApi}
                handleChangeDateFilter={handleChangeDateFilter}
                handleClickNextOrPrevDate={handleClickNextOrPrevDate}
                selectedDate={selectedDate}
            />
            <StyledCalendar
                // @ts-ignore
                ref={calendarRef}
                dateClick={handleDateClick}
                initialDate={modalSelectedDate}
                moreLinkClick={handleMoreClick}
                moreLinkContent={(arg) => `+${arg.num} ${calendarTexts.more}`}
                events={calendarParsedEvents}
                eventClick={handleEventClick}
            />
            {isEventsDataFetching && <ComponentBlockingAppLoader />}
        </AppCalendarContext.Provider>
    );
};
